import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';

function Policy() {
  usePageMeta({
    title: 'Acceptable Use Policy | WearTwice',
    description: "Read WearTwice's Acceptable Use Policy covering what can be listed, prohibited items and platform rules for buyers and sellers in the UAE.",
    path: '/acceptable-use-policy',
  });

  return (
    <>
      <section className="relative w-full h-auto bg-[#47715B] p-8">
        <div className="max-w-3xl mx-auto bg-[#47715B] rounded-lg p-6 text-white">
          <h1 className="text-2xl font-bold mb-4">Weartwice - Acceptable Use Policy</h1>

          <div className="p-6 max-w-4xl mx-auto text-white">
            <h2 className="text-xl font-bold mb-4">ACCEPTABLE USE POLICY</h2>
            <p className="mb-4">
              Weartwice aims to create a friendly and safe environment for trading second-hand
              items. To maintain the quality of the marketplace, members are required to follow
              the acceptable use policy below when uploading their items for sale.
            </p>
            <p className="mb-6">
              Please note that these rules are part of our Terms & Conditions. By creating an
              account on Weartwice, you agree to comply with these rules as well as our Terms & Conditions.
            </p>

            <h2 className="text-xl font-bold mb-4">ALLOWED ITEMS</h2>
            <p className="mb-4">You can sell the following on Weartwice:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Women's, men's, and children's clothing, footwear, and accessories.</li>
              <li>New cosmetics, beauty products, beauty devices, and gadgets.</li>
            </ul>
            <p className="mb-4">
                All items must be authentic and must not infringe trademarks, copyrights, or design rights.
            </p>
            <p className="mb-4">
              Weartwice reserves the right to determine which items are allowed or prohibited on
              the platform. If we discover that certain items violate our terms or pose risks to
              users, we may remove them even if they are not explicitly listed below.
            </p>
            <p className="mb-6">
              Additionally, items must have been lawfully placed on the UAE market before being
              listed on Weartwice. Items cannot enter the UAE market for the first time by being
              sold through Weartwice. Items must comply with applicable regulations to be sold on
              the platform.
            </p>
            <div className="text-left pl-5 space-y-2">
                <p className="font-semibold">Accuracy of Item Descriptions</p>
                <p className="mb-6">
                    Sellers must provide a truthful, complete, and accurate description of each item. This includes the item’s actual condition, size, material, signs of wear, defects, alterations, and any feature that may influence the Buyer’s decision. Any omission or misleading description is strictly prohibited and may result in removal of the listing and further action.
                </p>
            </div>
            <div className="text-left pl-5 space-y-2">
                <p className="font-semibold">Photo Requirements</p>
                <p className="mb-6">
                    All photos must be original and taken directly by the Seller using a mobile phone or camera. The use of images taken from the internet, screenshots, catalogue photos, brand visuals, or AI-generated content is strictly prohibited. Photos must show the item’s real condition and clearly display any flaws or imperfections. Misleading, heavily edited, or altered photos are not permitted.
                </p>
            </div>
            <div className="text-left pl-5 space-y-2">
                <p className="font-semibold">No Swaps or Trades</p>
                <p className="mb-6">
                    Swaps, trades, exchanges, gifts, or any non-monetary transactions are strictly prohibited. Only sales completed through Weartwice’s payment and shipping systems are permitted. Engaging in off-platform or non-monetary transactions prevents Weartwice from verifying delivery, protecting Buyers, or resolving disputes, and may result in corrective action.
                </p>
            </div>

            <h2 className="text-xl font-bold mb-4">PROHIBITED ITEMS</h2>
            <p className="mb-4">
              You cannot sell the following items on Weartwice (this list is not exhaustive and is
              intended as a guide):
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">
                <strong>Illegal items:</strong> Any goods or materials that are prohibited by law,
                such as counterfeit items, stolen goods, or products subject to trade sanctions.
              </li>
              <li className="mb-2">
                <strong>Restricted items:</strong> Items that are unrelated to fashion, beauty,
                homeware, entertainment, electronics, or other categories aligned with the Weartwice
                vision, such as industrial equipment or real estate.
              </li>
              <li className="mb-2">
                <strong>Unsafe items:</strong> Products that do not meet hygiene standards or may
                pose health or safety risks, such as used cosmetics or defective electronics.
              </li>
            </ul>
            <p className="mb-4">
              Listings suspected of being counterfeit may be removed immediately, even without prior notice.
            </p>

            <h2 className="text-xl font-bold mb-4">REPORTING COUNTERFEIT ITEMS</h2>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">
                Users may report listings suspected of being counterfeit via the “Report” feature.
              </li>
              <li className="mb-2">
                Reported listings will be reviewed by Weartwice.
              </li>
              <li className="mb-2">
                Weartwice may request additional evidence from both seller and buyer.
              </li>
              <li className="mb-2">
                Weartwice may remove the listing, suspend the seller, or take other action.
              </li>
            </ul>
            <p className="mb-4">
              Please ensure your listings comply with these acceptable use policy. Other members may
              flag a listing that appears to violate these rules for Artifice's review. If your
              listing is found to be non-compliant, it may be removed or hidden by our team. You may
              also face additional actions, such as account suspension or blocking.
            </p>
            <p className="mb-6">
              If you send messages promoting the sale of items that are part of the prohibited items
              list, you may face similar consequences. For more information, please review our
              Community Standards.
            </p>
            <div className="text-left pl-5 space-y-2">
                <p className="font-semibold">User Behaviour Standards</p>
                <p className="mb-6">
                    Users must act respectfully, responsibly, and in good faith when interacting through the Platform. Abusive, threatening, discriminatory, or otherwise inappropriate conduct is strictly prohibited and may lead to restrictions or suspension.
                </p>
            </div>

            <p className="mb-4">
              <strong>Please note:</strong> If you bump a listing that violates our acceptable use
              policy and it is subsequently removed or hidden, we will not be able to refund the
              cost of the bump.
            </p>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <Link to="/" className="bg-white text-[#47715B] px-4 py-2 rounded-lg shadow hover:bg-gray-200 transition duration-300">
            Back to Home
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-green-800 mx-auto text-center py-12 space-y-12">
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm md:text-base">
          <li><Link className="hover:underline text-white font-bold" to="/shipping-policy">Shipping policy</Link></li>
          <li><Link className="hover:underline text-white font-bold" to="/users-terms-conditions">Users terms and conditions</Link></li>
          <li><Link className="hover:underline text-white font-bold" to="/acceptable-use-policy">Acceptable use policy</Link></li>
          <li><Link className="hover:underline text-white font-bold" to="/privacy-policy">Privacy Policy</Link></li>
        </ul>
        <div className="mt-6">
          <img alt="Weartwice Logo" loading="lazy" width="200" height="50" className="mx-auto" src="/images/logo_full.svg" />
        </div>
        <div className="mt-6 text-xs md:text-sm text-white">© 2024 Weartwice. All rights reserved.</div>
      </footer>
    </>
  );
}

export default Policy;
